import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import data from "../../data.json";
import { IoIosArrowDropup } from "react-icons/io";
import { useSwipeable } from "react-swipeable";
import LanguageSelector from "../global/LanguageSelector";
import LogoTicker from "./LogoTicker";
import ImageGroup from "./ImageGroup";
import VideoModal from "../global/VideoModal";
import PoweredBy from "../global/PoweredBy";
import MultiVideoModal from "../global/MultiVideoModal";
// import { useDrag } from "@use-gesture/react";

const Home = () => {
  const { t, i18n } = useTranslation();
  const [showType, setShowType] = useState(true);
  const [showLogoTicker, setShowLogoTicker] = useState(true);
  const [options, setOptions] = useState([]);
  const [history, setHistory] = useState([]); // History stack for navigation
  const [logoClick, setLogoClick] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showMultiVideo, setShowMultiVideo] = useState(false);
  const [currentOption, setCurrentOption] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentBgVideo, setCurrentBgVideo] = useState("/homevideo_small.mp4");
  const [referrer, setReferrer] = useState("");
  const [isDesktop, setIsDesktop] = useState(false);
  const currentLanguage = i18n.language || "en";
  const [buttonClickName, setButtonClickName] = useState(null);

  // Function to check if screen is desktop size (768px and above)
  const checkScreenSize = () => {
    return window.innerWidth >= 568;
  };

  // Function to get video path based on screen size
  const getVideoPath = (videoName, isDesktop) => {
    const folder = isDesktop ? '/desktop_video/' : '/';
    return `${folder}${videoName}`;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(checkScreenSize());
    };

    // Initial screen size check
    setIsDesktop(checkScreenSize());

    // Listen for window resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("referrer");
    if (ref) {
      setReferrer(ref);
      handleLogoClickOrSwipe();
    }
  }, []);

  useEffect(() => {
    let videoName = '';
    if (history?.length <= 1 && !logoClick) {
      videoName = 'homevideo_small.mp4';
    } else if (history?.length <= 1 && logoClick) {
      videoName = 'homevideo.mp4';
    } else if (buttonClickName === "motor") {
      videoName = 'homevideo_vehicle.mp4';
    } else if (buttonClickName === "property") {
      videoName = 'homevideo_prpt.mp4';
    } else {
      videoName = 'homevideo.mp4';
    }
    
    setCurrentBgVideo(getVideoPath(videoName, isDesktop));
  },[buttonClickName, history, logoClick, isDesktop]);

  useEffect(() => {
    const updateViewportHeight = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty("--vh", `${vh * 0.01}px`);
    };

    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  const handleOptionClick = (option) => {
    if (option?.label === "motor") {
      setButtonClickName("motor");
    }
    if (option?.label === "property") {
      setButtonClickName("property");
    }
    if (option?.label === "airline_solution" || option?.label === "oem") {
      setCurrentVideo(`/video/${option.label}.mp4`);
      setShowVideo(true);
      return;
    }
    if (
      option?.label === "photogrammetry" ||
      option?.label === "video_assessment"
    ) {
      setShowMultiVideo(true);
      setCurrentOption(option.label);
      return;
    }
    if (option?.url) {
      const currentLanguage = (i18n.language || "en").split("-")[0];
      const newUrl = option.url?.replace(
        /\/[a-z]{2}(?:-[a-z]{2})?\//i,
        `/${currentLanguage}/`
      );
      if (newUrl) {
        window.location.assign(newUrl);
      } else {
        window.location.assign(option.url);
      }
    } else {
      setHistory((prev) => [...prev, options]); // Push current options to history
      setOptions(option);
      setShowType(false);
    }
  };
  const handleLogoClickOrSwipe = () => {
    setLogoClick(true);
    setShowLogoTicker(false);
  };
  const handleBackClick = () => {
    if (history?.length === 0) {
      setLogoClick(false);
      setShowLogoTicker(true);
      setShowType(true);
    } else if (history?.length > 1) {
      const lastOptions = history[history.length - 1];
      setHistory((prev) => prev.slice(0, -1)); // Pop the last state from history
      setOptions(lastOptions);
    } else {
      setHistory((prev) => prev.slice(0, -1));
      setShowType(true);
      setShowLogoTicker(true);
    }
  };

  const handleModalClose = () => {
    setShowVideo(false);
  };

  const handlers = useSwipeable({
    onSwipedUp: handleLogoClickOrSwipe,
    trackMouse: true,
  });

  const handleMultiVideoModalClose = () => {
    setShowMultiVideo(false);
  };

  const handleCamcomLogoClick = () => {
    // Navigate to the initial state
    setHistory([]); // Clear history
    setOptions([]); // Clear current options
    setShowType(true); // Show initial options
    setShowLogoTicker(true); // Show logo ticker
    setLogoClick(false); // Reset logo click state
    setShowVideo(false); // Close any open video
    setShowMultiVideo(false); // Close any open multi-video
    setCurrentOption(null); // Clear current option
    setCurrentVideo(null); // Clear current video
  };

  const lastOptions = history[history.length - 1];

  if (!i18n.isInitialized) return <div>Loading translations...</div>;

  const isRULang = localStorage.getItem("i18nextLng") === "ru";
  return (
    <>
      {/* <LoadingScreen> */}
      <style>{`body { background: var(--primary-black); }`}</style>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.5 }}
        className="fixed"
      >
        {/* <img
          src="/cover_bg.png"
          alt="Company Logo"
          width={2532}
          height={1170}
          className="fixed inset-0 z-10 size-full object-cover"
        /> */}

        <video
          // className="w-full h-auto sm:fixed sm:inset-0 sm:h-full sm:w-full sm:object-cover sm:z-0"
          className="fixed inset-0 h-full w-full object-cover z-0"
          key={currentBgVideo}
          muted={true}
          autoPlay={true}
          playsInline={true}
          loop={true}
          preload="auto"
        >
          <source src={currentBgVideo} type="video/mp4" />
        </video>
      </motion.div>

      <div className="fixed top-48 md:top-[14%] w-full flex justify-center z-10">
        <ImageGroup />
      </div>
      <div
        className=" w-full relative z-10 flex items-center h-[calc(100vh_-_60px)] overflow-hidden justify-center"
        {...(!logoClick ? handlers : {})}
        // style={{ height: "calc(var(--vh, 1vh) * 100)", overflow: "hidden" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut", delay: 1.5 }}
        >
          <LanguageSelector />
        </motion.div>

        <div className="flex mt-20 flex-col justify-center gap-10 h-full w-full text-white pt-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 2 }}
            className="px-6 pb-6 pt-0 fixed top-12 left-0"
          >
            <img
              // src="/camcom-logo.png"
              src="/camcom-logo.png"
              alt="Camcom Logo"
              className="h-[40px] p-0.5 bg-white object-contain"
              onClick={handleCamcomLogoClick}
            />
          </motion.div>
          {/* <div className="mt-10">
          <ImageGroup />
          </div> */}
          {/* {!logoClick && <LogoTicker />} */}
          {!logoClick && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut", delay: 2 }}
              className={`flex flex-col items-center justify-center text-center px-6 ${isRULang ? "mb-[1%]" :"mb-[15%] md:mb-[1%]"}`}
            >
              <h1
                className="font-normal text-2xl md:text-2xl sm:text-lg tracking-tight bg-white/20 backdrop-blur-[4px] backdrop-saturate-100 rounded-xl border border-white/20 px-4 py-3"
                dangerouslySetInnerHTML={{ __html: t("welcome_message") }}
              />
            </motion.div>
          )}
          <div className="flex flex-col justify-end max-h-[360px] w-[92%] md:w-[70%] lg:w-[40%] mb-[10%]  mx-auto">
            {logoClick && (
              <motion.div
                className="flex flex-col gap-2 bg-white/20 backdrop-blur-[16px] backdrop-saturate-100 rounded-xl border border-white/20 justify-center items-center text-center h-full w-full md:overflow-visible overflow-y-auto pt-1 mt-[28%]"
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "100%", opacity: 0 }}
                transition={{
                  type: "tween",
                  duration: 2,
                  ease: "easeInOut",
                }}
              >
                <h1
                  className="font-normal leading-none text-lg pt-3"
                  dangerouslySetInnerHTML={{ __html: t("welcome_message") }}
                />

                <div className="w-full max-h-[calc(120vh-200px)] overflow-y-auto flex flex-col items-center">
                  <AnimatePresence>
                    {showType ? (
                      <motion.div
                        className="w-full h-[auto] flex flex-col justify-center items-center gap-4"
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{
                          type: "tween",
                          duration: 1,
                          ease: "easeInOut",
                        }}
                      >
                        <div className="flex px-3 pb-4 pt-3 flex-wrap w-full portrait:h-[auto] justify-between gap-4">
                          {data &&
                            data.map((option) => (
                              <motion.button
                                key={option.id}
                                onClick={() => handleOptionClick(option)}
                                className={`w-[47%] px-2 py-1 md:py-2 rounded-lg text-black font-normal break-words text-base ${
                                  option.disabled ? "bg-gray-500" : "bg-white"
                                }`}
                                disabled={option.disabled}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{
                                  type: "tween",
                                  duration: 1,
                                  ease: "easeInOut",
                                }}
                              >
                                {t(option.label)}
                              </motion.button>
                            ))}
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        className="w-full px-3 pt-3 pb-4 flex flex-col items-center gap-3"
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{
                          type: "tween",
                          duration: 1,
                          ease: "easeInOut",
                        }}
                      >
                        <div className="w-full flex justify-center items-center gap-4 px-4">
                          {lastOptions.label &&
                            lastOptions.label !== options.label && (
                              <>
                                <motion.button
                                  className="min-w-[47%] px-2 py-1 md:py-2 rounded-lg bg-green-700 text-white border-white border-2 font-normal break-words text-sm cursor-default"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{
                                    type: "tween",
                                    duration: 1,
                                    ease: "easeInOut",
                                  }}
                                >
                                  {t(lastOptions.label)}
                                </motion.button>
                                <motion.button
                                  className="text-white text-xl cursor-default"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{
                                    type: "tween",
                                    duration: 1,
                                    ease: "easeInOut",
                                  }}
                                >
                                  &gt;
                                </motion.button>
                              </>
                            )}

                          <motion.button
                            key={`current-${options.label}`}
                            className="min-w-[47%] px-2 py-1 md:py-2 rounded-lg bg-green-700 cursor-default text-white border-white border-2 font-normal break-words text-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                              type: "tween",
                              duration: 1,
                              ease: "easeInOut",
                            }}
                          >
                            {t(options.label)}
                          </motion.button>
                        </div>
                        <div className="flex flex-wrap w-full gap-4 mt-3 justify-center">
                          {options.submenu.map((submenuOption, index) => (
                            <motion.button
                              key={index}
                              onClick={() => handleOptionClick(submenuOption)}
                              disabled={submenuOption.disabled}
                              className={`w-[47%] py-1 md:py-2 rounded-lg text-black font-normal text-sm break-words ${
                                submenuOption.disabled
                                  ? "bg-gray-500"
                                  : "bg-white"
                              } ${currentLanguage === "de" ? "px-0" : "px-2"}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{
                                type: "tween",
                                duration: 1,
                                ease: "easeInOut",
                              }}
                            >
                              {t(submenuOption.label)}
                            </motion.button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
            {/* <PoweredBy /> */}

            <div className="absolute w-full flex justify-end bottom-[4%] right-0 mr-5 ">
              {logoClick && (
                <motion.button
                  onClick={handleBackClick}
                  className="w-[30%] h-7 flex items-center justify-center landscape:w-[10%] px-2 py-0.5 rounded-lg text-white text-base font-semibold border-white border-2 bg-black"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{
                    type: "tween",
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                >
                  {t("back")}
                </motion.button>
              )}
            </div>
          </div>
          {!logoClick && (
            <>
              {/* Move LogoTicker to bottom above swipe-up button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut", delay: 2 }}
                className="fixed bottom-40 w-full z-20"
              >
                <LogoTicker />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut", delay: 2 }}
                {...handlers}
                className="fixed bottom-14 flex items-center justify-center w-full transform -translate-x-1/2 z-30"
              >
                <motion.div
                  className="cursor-pointer flex flex-col justify-center items-center w-10 py-2 rounded-full bg-black shadow-lg gap-1"
                  onClick={handleLogoClickOrSwipe}
                  initial={{ y: 0 }}
                  animate={{ opacity: 1, y: [0, -10, 0] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1,
                    ease: "easeInOut",
                  }}
                >
                  <IoIosArrowDropup className="text-white h-8 w-8" />
                  <p className="text-white text-sm font-semibold mt-2 text-nowrap absolute -bottom-4">
                    {isDesktop ? t("lets_begin") : t("swipe")}
                  </p>
                </motion.div>
              </motion.div>
            </>
          )}
        </div>
      </div>
      {showVideo && currentVideo && (
        <VideoModal onClose={handleModalClose} currentVideo={currentVideo} />
      )}
      {showMultiVideo && (
        <div className="w-screen h-screen bg-gray-900 overflow-hidden">
          <MultiVideoModal option={currentOption} on />
          <MultiVideoModal
            option={currentOption}
            onClose={handleMultiVideoModalClose}
          />
        </div>
      )}
      {/* </LoadingScreen> */}
    </>
  );
};

export default Home;
